import { createAction } from "redux-actions";
import humps from "humps";
import moment from "moment";
import { apiGet, apiPost } from "util/api";
import { isSeabird, handleSeabirdAnnotation } from "util/seabird";
import { isRBR, isRsk, handleRBRAnnotation } from "util/rbr";
import * as types from "./constants";
import annotatePropsSelector from "./selectors";
/* eslint-disable import/no-cycle */
import { uploadFilesInvalidate } from "../upload/actions";
import { downloadCastsInvalidate } from "../download/actions";

/* eslint-disable max-len */
export const openAnnotationModal = createAction(types.ANNOTATE_MODAL_OPEN);
export const closeAnnotationModal = createAction(types.ANNOTATE_MODAL_CLOSE);
export const setAnnotationModalCast = createAction(
  types.ANNOTATE_MODAL_SET_CAST
);
export const setAnnotationModalForms = createAction(
  types.ANNOTATE_MODAL_SET_FORMS
);
export const setAnnotationModalSelected = createAction(
  types.ANNOTATE_MODAL_SET_SELECTED
);
export const openChartModal = createAction(types.CHART_MODAL_OPEN);
export const closeChartModal = createAction(types.CHART_MODAL_CLOSE);
export const openMapModal = createAction(types.MAP_MODAL_OPEN);
export const closeMapModal = createAction(types.MAP_MODAL_CLOSE);
export const setChartModalCast = createAction(types.CHART_MODAL_SET_CAST);
export const updateChartModalCast = createAction(types.CHART_MODAL_UPDATE_CAST);
export const setMapModalCast = createAction(types.MAP_MODAL_SET_CAST);
export const setMapForm = createAction(types.MAP_MODAL_SET_FORM);
export const openAnnotationSnackbar = createAction(
  types.ANNOTATE_SNACKBAR_OPEN
);
export const closeAnnotationSnackbar = createAction(
  types.ANNOTATE_SNACKBAR_CLOSE
);
export const annotateFilesRequest = createAction(types.ANNOTATE_FILES_REQUEST);
export const annotateFilesSuccess = createAction(types.ANNOTATE_FILES_SUCCESS);
export const annotateFilesError = createAction(
  types.ANNOTATE_FILES_ERROR,
  x => x,
  (x, m) => m
);
export const filesToAnnotateInvalidate = createAction(
  types.ANNOTATE_FILES_INVALIDATE
);
export const filesToAnnotateFetchRequest = createAction(
  types.ANNOTATE_FILES_FETCH_REQUEST
);
export const filesToAnnotateFetchSuccess = createAction(
  types.ANNOTATE_FILES_FETCH_SUCCESS
);
export const filesToAnnotateFetchError = createAction(
  types.ANNOTATE_FILES_FETCH_ERROR
);
export const castsToAnnotateFetchRequest = createAction(
  types.ANNOTATE_CASTS_FETCH_REQUEST
);
export const castsToAnnotateFetchSuccess = createAction(
  types.ANNOTATE_CASTS_FETCH_SUCCESS
);
export const castsToAnnotateFetchError = createAction(
  types.ANNOTATE_CASTS_FETCH_ERROR,
  x => x,
  (x, m) => m
);
export const setMatchedForm = createAction(
  types.ANNOTATE_CASTS_SET_MATCHED_FORM
);
export const updateMatchedForm = createAction(
  types.ANNOTATE_CASTS_UPDATE_MATCHED_FORM
);
export const updateCasts = createAction(types.ANNOTATE_CASTS_UPDATE_CAST);

export const addFileToAnnotate = createAction(
  types.ANNOTATE_ADD_FILE_TO_ANNOTATE
);
export const removeFileToAnnotate = createAction(
  types.ANNOTATE_REMOVE_FILE_TO_ANNOTATE
);
export const chartDataFetchRequest = createAction(
  types.CHART_DATA_FETCH_REQUEST
);
export const chartDataFetchSuccess = createAction(
  types.CHART_DATA_FETCH_SUCCESS
);
export const chartDataFetchError = createAction(types.CHART_DATA_FETCH_ERROR);
export const mapSiteDataFetchRequest = createAction(
  types.MAP_SITE_DATA_FETCH_REQUEST
);
export const mapSiteDataFetchSuccess = createAction(
  types.MAP_SITE_DATA_FETCH_SUCCESS
);
export const mapSiteDataFetchError = createAction(
  types.MAP_SITE_DATA_FETCH_ERROR
);
/* eslint-enable max-len */

// ASYNC ACTIONS FOR FETCHING FILES NEEDING ANNOTATATION
const verifyS3Upload = filesToAnnotate => dispatch => {
  // send all filenames to s3 endpoint to verify rsk or hex has also been uploaded
  apiPost(`/ctd/s3/verify`, filesToAnnotate, { responseType: "json" })
    // .then dispatch files to annotate success
    .then(res => res.json())
    .then(json => dispatch(filesToAnnotateFetchSuccess(json)));
};
const filesToAnnotateFetch = () => (dispatch, getState) => {
  const { workarea, organization } = annotatePropsSelector(getState());
  dispatch(filesToAnnotateFetchRequest());
  apiGet(
    `/ctd/views/files_to_annotate?work_area=${workarea}&organization=${organization}&fields=filename,ctd_file_pk,shutoff,file_bytes,collection_dt,num_casts&limit=-1`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(json => dispatch(verifyS3Upload(json)))
    .catch(err => dispatch(filesToAnnotateFetchError(err)));
};
export const filesToAnnotateFetchIfNeeded = () => (dispatch, getState) => {
  const state = getState();
  const {
    filesList,
    filesDidInvalidate,
    filesFetching
  } = annotatePropsSelector(state);
  if ((filesList.length <= 0 || filesDidInvalidate) && !filesFetching) {
    dispatch(filesToAnnotateFetch());
  }
};

// ASYNC ACTIONS FOR FETCHING CASTS AND POSSIBLY MATCHING FORMS FOR A SPECIFIC FILE
const castsToAnnotateFetch = ({ ctdFilePk }) => (dispatch, getState) => {
  const { workarea, organization } = annotatePropsSelector(getState());
  dispatch(castsToAnnotateFetchRequest({ ctdFilePk }));
  apiGet(
    `/ctd/views/casts_to_annotate?work_area=${workarea}&organization=${organization}&ctd_file_pk=${ctdFilePk}&limit=-1`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(json =>
      dispatch(
        castsToAnnotateFetchSuccess(
          json.map(c => ({
            ...c,
            matchedForm: c.possibleForms[c.mostLikelyMatchIndex]
          }))
        )
      )
    )
    .catch(err => {
      return dispatch(castsToAnnotateFetchError(err));
    });
};
export const castsToAnnotateFetchIfNeeded = ({ ctdFilePk }) => (
  dispatch,
  getState
) => {
  const state = getState();
  const { savingFile, fetchedFilePks } = annotatePropsSelector(state);
  if (!savingFile && !fetchedFilePks.includes(ctdFilePk)) {
    dispatch(castsToAnnotateFetch({ ctdFilePk }));
  }
};

// ASYNC ACTION FOR FETCHING DATA TO DISPLAY CAST CHART
export const chartDataFetch = ({
  ctdFilePk,
  startDt,
  bottomDt
}) => dispatch => {
  const startWindow = moment(startDt)
    .subtract(3, "m")
    .format();
  const endWindow = moment(bottomDt)
    .add(3, "m")
    .format();
  dispatch(chartDataFetchRequest({ ctdFilePk }));
  apiGet(
    `/ctd/views/file/raw_data?limit=-1&ctd_file_pk=${ctdFilePk}&measurement_dt>=${startWindow}&measurement_dt<=${endWindow}&sort=measurement_dt&fields=measurement_dt,depth`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(data => data.map(d => ({ ...d, depth: -d.depth })))
    .then(json => dispatch(chartDataFetchSuccess(json)))
    .catch(err => dispatch(chartDataFetchError(err)));
};

export const mapSiteDataFetch = (lat, long, radius) => async dispatch => {
  dispatch(mapSiteDataFetchRequest());
  await apiGet(
    `/eims/views/output/sites_in_radius?geom.within_radius(${lat}, ${long}, ${radius})&limit=0`
  )
    .then(res => res.json())
    .then(json => dispatch(mapSiteDataFetchSuccess(json)))
    .catch(err => dispatch(mapSiteDataFetchError(err)));
};

// ASYNC ACTION FOR FETCHING DATA TO DISPLAY CAST CHART - ADDITIONAL MINUTE AT START
export const chartDataFetchAddStart = ({
  ctdFilePk,
  startDt,
  chartData
}) => dispatch => {
  const startWindow = moment(startDt)
    .subtract(3, "m")
    .format();
  const endWindow = moment(startDt).format();
  dispatch(chartDataFetchRequest({ ctdFilePk }));
  apiGet(
    `/ctd/views/file/raw_data?limit=-1&ctd_file_pk=${ctdFilePk}&measurement_dt>=${startWindow}&measurement_dt<=${endWindow}&sort=measurement_dt&fields=measurement_dt,depth`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(data => data.map(d => ({ ...d, depth: -d.depth })))
    .then(data => {
      const combinedData = [...data, ...chartData];
      const mTimes = combinedData.map(o => o.measurementDt);
      const newData = combinedData.filter(
        ({ measurementDt }, index) => !mTimes.includes(measurementDt, index + 1)
      );
      if (newData.length <= chartData.length) {
        dispatch(updateChartModalCast({ addStartDisabled: true }));
      }
      return newData;
    })
    .then(json => dispatch(chartDataFetchSuccess(json)))
    .catch(err => dispatch(chartDataFetchError(err)));
};

// ASYNC ACTION FOR FETCHING DATA TO DISPLAY CAST CHART - ADDITIONAL MINUTE AT END
export const chartDataFetchAddEnd = ({
  ctdFilePk,
  endDt,
  chartData
}) => dispatch => {
  const startWindow = moment(endDt).format();
  const endWindow = moment(endDt)
    .add(3, "m")
    .format();
  dispatch(chartDataFetchRequest({ ctdFilePk }));
  apiGet(
    `/ctd/views/file/raw_data?limit=-1&ctd_file_pk=${ctdFilePk}&measurement_dt>=${startWindow}&measurement_dt<=${endWindow}&sort=measurement_dt&fields=measurement_dt,depth`
  )
    .then(res => res.json())
    .then(humps.camelizeKeys)
    .then(data => data.map(d => ({ ...d, depth: -d.depth })))
    .then(data => {
      const combinedData = [...chartData, ...data];
      const mTimes = combinedData.map(o => o.measurementDt);
      const newData = combinedData.filter(
        ({ measurementDt }, index) => !mTimes.includes(measurementDt, index + 1)
      );
      if (newData.length <= chartData.length) {
        dispatch(updateChartModalCast({ addEndDisabled: true }));
      }
      return newData;
    })
    .then(json => dispatch(chartDataFetchSuccess(json)))
    .catch(err => dispatch(chartDataFetchError(err)));
};

// ASYNC ACTION TO ANNOTATE THE FILE IN THE database
export const annotateFiles = () => (dispatch, getState) => {
  const {
    filesBeingAnnotated: ctdFilePks,
    filesToAnnotate,
    castsInFiles,
    matchedForms,
    workarea
  } = annotatePropsSelector(getState());

  const files = ctdFilePks.map(pk => ({
    ctdFilePk: filesToAnnotate[pk].ctdFilePk,
    filename: filesToAnnotate[pk].filename,
    casts: Object.keys(castsInFiles[pk]).map(cast => ({
      ctdCastPk: castsInFiles[pk][cast].ctdCastPk,
      matchedForm: matchedForms[castsInFiles[pk][cast].ctdCastPk],
      bottomDt: castsInFiles[pk][cast].bottomDt,
      startDt: castsInFiles[pk][cast].startDt,
      endDt: castsInFiles[pk][cast].endDt,
      manualOverrideDt: castsInFiles[pk][cast].manualOverrideDt
    }))
  }));

  files.forEach(f => {
    const { filename, ctdFilePk, casts } = f;

    // Route annotation logic to specific functions for each CTD instrument
    if (isSeabird({ name: filename })) {
      Promise.resolve(dispatch(annotateFilesRequest({ ctdFilePk })))
        .then(() => handleSeabirdAnnotation(casts, workarea))
        .then(() => dispatch(annotateFilesSuccess({ ctdFilePk, filename })))
        .then(() => dispatch(uploadFilesInvalidate()))
        .then(() => dispatch(downloadCastsInvalidate()))
        .then(() => dispatch(openAnnotationSnackbar()))
        .catch(err => dispatch(annotateFilesError(err, { ctdFilePk })));
    } else if (isRBR({ name: filename }) || isRsk({ name: filename })) {
      Promise.resolve(dispatch(annotateFilesRequest({ ctdFilePk })))
        .then(() => handleRBRAnnotation(casts, workarea))
        .then(() => dispatch(annotateFilesSuccess({ ctdFilePk, filename })))
        .then(() => dispatch(uploadFilesInvalidate()))
        .then(() => dispatch(downloadCastsInvalidate()))
        .then(() => dispatch(openAnnotationSnackbar()))
        .catch(err => dispatch(annotateFilesError(err, { ctdFilePk })));
    } else {
      dispatch(
        annotateFilesError(new Error("Unable to tell CTD type from filename"))
      );
    }
  });
};
